/**
 * StoreFinderModal Module - Following guidelines
 * Pure Tailwind, no inline styles, array mapping
 */

import { debugLog } from '../debug/logger.js'

class StoreFinderModal {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SELECTORS: {
				trigger: '.ui-storefinder-trigger',
				modal: '#storefinder-modal',
				backdrop: '#storefinder-backdrop',
				content: '#storefinder-content',
				closeBtn: '.ui-storefinder-close',
				iframe: '#storefinder-iframe',
			},
			STATES: {
				modal: {
					open: ['block', 'opacity-100'],
					closed: ['hidden', 'opacity-0'],
				},
				backdrop: {
					open: ['opacity-100', 'visible'],
					closed: ['opacity-0', 'invisible'],
				},
				content: {
					open: ['scale-100', 'opacity-100'],
					closed: ['scale-95', 'opacity-0'],
				},
			},
			TIMING: {
				ANIMATION: 300,
			},
			URLS: {
				storefinder: '/storefinder', // Update with actual StoreFinder URL
			},
		}

		// State
		this.isOpen = false

		// Get translations from trigger button
		const trigger = document.querySelector('.ui-storefinder-trigger')
		this.translations = {
			storefinder: trigger?.dataset.i18nStorefinder || 'Store Finder',
			closeStorefinder: trigger?.dataset.i18nCloseStorefinder || 'Close store finder',
			storefinderError: trigger?.dataset.i18nStorefinderError || 'Failed to load Store Finder',
		}

		// Create modal if doesn't exist
		this.createModal()

		// Get elements (after modal is created)
		this.elements = this.getElements()

		// Initialize
		this.init()
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		return {
			triggers: document.querySelectorAll(SELECTORS.trigger),
			modal: document.querySelector(SELECTORS.modal),
			backdrop: document.querySelector(SELECTORS.backdrop),
			content: document.querySelector(SELECTORS.content),
			closeBtns: document.querySelectorAll(SELECTORS.closeBtn),
			iframe: document.querySelector(SELECTORS.iframe),
		}
	}

	createModal() {
		// Check if already exists
		if (document.getElementById('storefinder-modal')) {
			return
		}

		// Create modal HTML
		const modalHTML = `
			<div id="storefinder-modal" class="fixed inset-0 z-[200] hidden overflow-y-auto">
				<!-- Backdrop -->
				<div id="storefinder-backdrop"
					class="fixed inset-0 bg-black bg-opacity-50 transition-all duration-300 opacity-0 invisible"></div>

				<!-- Modal Content -->
				<div class="flex min-h-screen items-center justify-center p-4">
					<div id="storefinder-content"
						class="relative w-full max-w-6xl transform bg-white rounded-lg shadow-2xl transition-all duration-300 scale-95 opacity-0">

						<!-- Header -->
						<div class="flex items-center justify-between border-b border-gray-200 p-4">
							<h2 class="text-xl font-semibold text-gray-900">${this.translations.storefinder}</h2>
							<button class="ui-storefinder-close p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
								aria-label="${this.translations.closeStorefinder}">
								<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
								</svg>
							</button>
						</div>

						<!-- Content -->
						<div class="relative h-[600px] p-4">
							<div class="h-full w-full rounded-lg bg-gray-100 flex items-center justify-center">
								<div id="storefinder-loading" class="text-center">
									<div class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
									<p class="mt-2 text-gray-600">Loading Store Finder...</p>
								</div>
								<iframe id="storefinder-iframe"
									class="hidden h-full w-full rounded-lg border-0"
									title="${this.translations.storefinder}"></iframe>
							</div>
						</div>
					</div>
				</div>
			</div>
		`

		// Add to DOM
		document.body.insertAdjacentHTML('beforeend', modalHTML)
	}

	init() {
		if (this.elements.triggers.length === 0) {
			return
		}

		// Bind events
		this.bindEvents()
	}

	bindEvents() {
		// Trigger buttons
		this.elements.triggers.forEach(trigger => {
			trigger.addEventListener('click', e => {
				e.preventDefault()
				debugLog.info('StoreFinderModal: Trigger clicked')
				this.open()
			})
		})

		// Close buttons
		this.elements.closeBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				this.close()
			})
		})

		// Backdrop click
		if (this.elements.backdrop) {
			this.elements.backdrop.addEventListener('click', () => {
				this.close()
			})
		}

		// ESC key
		document.addEventListener('keydown', e => {
			if (e.key === 'Escape' && this.isOpen) {
				this.close()
			}
		})
	}

	open() {
		if (this.isOpen) return

		const { STATES, TIMING } = this.CONFIG

		// Show modal
		this.swapClasses(this.elements.modal, STATES.modal.closed, STATES.modal.open)

		// Fade in backdrop
		setTimeout(() => {
			this.swapClasses(this.elements.backdrop, STATES.backdrop.closed, STATES.backdrop.open)
		}, 10)

		// Scale in content
		setTimeout(() => {
			this.swapClasses(this.elements.content, STATES.content.closed, STATES.content.open)
		}, 50)

		// Load iframe content
		this.loadStoreFinder()

		// Prevent body scroll
		document.body.classList.add('overflow-hidden')

		this.isOpen = true

		debugLog.info('StoreFinderModal: Opened')
	}

	close() {
		if (!this.isOpen) return

		const { STATES, TIMING } = this.CONFIG

		// Scale out content
		this.swapClasses(this.elements.content, STATES.content.open, STATES.content.closed)

		// Fade out backdrop
		setTimeout(() => {
			this.swapClasses(this.elements.backdrop, STATES.backdrop.open, STATES.backdrop.closed)
		}, 100)

		// Hide modal
		setTimeout(() => {
			this.swapClasses(this.elements.modal, STATES.modal.open, STATES.modal.closed)
			// Clear iframe
			if (this.elements.iframe) {
				this.elements.iframe.src = ''
				this.elements.iframe.classList.add('hidden')
			}
			// Show loading
			const loading = document.getElementById('storefinder-loading')
			if (loading) {
				loading.classList.remove('hidden')
			}
		}, TIMING.ANIMATION)

		// Restore body scroll
		document.body.classList.remove('overflow-hidden')

		this.isOpen = false

		debugLog.info('StoreFinderModal: Closed')
	}

	loadStoreFinder() {
		if (!this.elements.iframe) return

		// Get StoreFinder URL from trigger or use default
		const trigger = Array.from(this.elements.triggers).find(t => t.dataset.storeFinderUrl)
		const url = trigger?.dataset.storeFinderUrl || this.CONFIG.URLS.storefinder

		// Add security headers for iframe
		this.elements.iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms allow-popups')
		this.elements.iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade')

		// Set iframe source
		this.elements.iframe.src = url

		// Hide loading when iframe loads
		this.elements.iframe.onload = () => {
			const loading = document.getElementById('storefinder-loading')
			if (loading) {
				loading.classList.add('hidden')
			}
			this.elements.iframe.classList.remove('hidden')
		}

		// Handle iframe errors
		this.elements.iframe.onerror = () => {
			debugLog.error('StoreFinderModal: Failed to load content')
			const loading = document.getElementById('storefinder-loading')
			if (loading) {
				loading.innerHTML = `<p class="text-red-600">${this.translations.storefinderError}</p>`
			}
		}
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new StoreFinderModal())
} else {
	new StoreFinderModal()
}

// Export for use
window.StoreFinderModal = StoreFinderModal
